import { useMemo, useState } from "react";
import ArrowDownIcon from "@assets/icons/new/arrow-down_10px.svg?react";
import ArrowUpIcon from "@assets/icons/new/arrow-up_10px.svg?react";
import { FaroIconButton } from "@components/common/faro-icon-button";
import { BaseProjectProps } from "@custom-types/project-types";
import { DeleteIcon, FolderSmallIcon } from "@faro-lotv/flat-ui";
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from "@mui/material";
import { useMediaQueryList } from "@hooks/use-media-query";
import { DataManagementDropzone } from "@pages/data-management/data-management-dropzone";
import {
  AnyFilesInfo,
  DataManagementUploadElementType,
  isInvalidFileInfo,
  UploadedIdsMap,
} from "@pages/data-management/data-management-types";
import { getFileSizeForFileInfos } from "@pages/data-management/data-management-utils";
import { getFallbackFolderName } from "@pages/data-management/import-data/import-data-utils";
import { sphereColors } from "@styles/common-colors";
import { withEllipsis } from "@styles/common-styles";
import { bytesToSizeString } from "@utils/file-utils";
import { SphereTooltip } from "@components/common/sphere-tooltip";

interface Props extends BaseProjectProps {
  /** A map of the externalIds of the uploaded ELS scans, as returned by getUploadedIdsMap. */
  uploadedIdsMap: UploadedIdsMap;
  /** File infos constructed by useOnCheckFiles. */
  fileInfos: AnyFilesInfo[],
  /** Callback for setting the file infos. */
  setFileInfos: React.Dispatch<React.SetStateAction<AnyFilesInfo[]>>,
  /** Flag whether the browser's file explorer is currently open. */
  isFileExplorerOpen: boolean;
  /** Callback for setting whether the file explorer is open. */
  setIsFileExplorerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Flag whether file info is currently getting collected and the dropzone should show a spinner. */
  isDropzoneLoading: boolean;
  /** Callback for setting whether file info is getting collected. */
  setIsDropzoneLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Component that displays the files that have been added by the user to be imported.
 * It includes a dropzone for uploading additional files and a listing of the added files, grouped by their type.
 */
export function AddedDataComponent({
  project,
  uploadedIdsMap,
  fileInfos,
  setFileInfos,
  isFileExplorerOpen,
  setIsFileExplorerOpen,
  isDropzoneLoading,
  setIsDropzoneLoading,
}: Props): JSX.Element {
  const { isScreenMdAndSmaller } = useMediaQueryList();
  const [isCollapsed, setIsCollapsed] = useState(true);

  // The files containing metadata are not included this way, but that should be negligible.
  const sizesTotal: Map<AnyFilesInfo, number> = useMemo(() => {
    return getFileSizeForFileInfos(fileInfos);
  }, [fileInfos]);

  const { groupedFileInfos, numInfos } = useMemo(() => {
    // Group file infos by their type
    const groups: Record<DataManagementUploadElementType, {
      title: string,
      items: AnyFilesInfo[]
    }> = {
      elsScan:        { title: "Blink data",          items: [] },
      focusScan:      { title: "Focus data",          items: [] },
      orbisScan:      { title: "Orbis data",          items: [] },
      photogrammetry: { title: "Photogrammetry data", items: [] },
    };

    let numInfos = 0;
    // Add all file infos to the corresponding group
    for (const info of fileInfos) {
      if (!isInvalidFileInfo(info)) {
        groups[info.elementType].items.push(info);
        numInfos++;
      }
    }

    return {
      // Only return groups that have items
      groupedFileInfos: Object.values(groups).filter((group) => group.items.length > 0),
      numInfos,
    };
  }, [fileInfos]);

  const listItems: JSX.Element[] = useMemo(() => {
    const items: JSX.Element[] = [];

    groupedFileInfos.forEach((group, groupIdx) => {
      // Add group header
      items.push(
        <ListItem
          key={`group-${groupIdx}`}
          sx={{
            mt: "25px",
            mb: "10px",
            pl: "0px",
            fontWeight: "700",
            textTransform: "uppercase",
        }}>
          {group.title}
        </ListItem>
      );

      // Add items in this group
      group.items.forEach((fileInfo, itemIdx) => {
        const importNameStr = fileInfo.projectName ?? fileInfo.folderName ?? getFallbackFolderName(fileInfo);
        const importName = <var title={importNameStr}>{importNameStr}</var>;
        const folderSize = <var>{bytesToSizeString(sizesTotal.get(fileInfo) || 0)}</var>;

        items.push(
          <ListItem
            key={`group-${groupIdx}-item-${itemIdx}`}
            secondaryAction={
              <Box sx={{ mr: "-10px" }}>
                <SphereTooltip title="Remove">
                  <FaroIconButton
                    dataTestId="sa-remove-folder"
                    isDisabled={isDropzoneLoading}
                    component={DeleteIcon}
                    onClick={() => {
                      setFileInfos((prev) => prev.filter((info) => info !== fileInfo));
                    }}
                  />
                </SphereTooltip>
              </Box>
            }
            sx={{
              mb: "3px",
              pt: "6px",
              pb: "6px",
              backgroundColor: sphereColors.gray50,
              border: `1px solid ${sphereColors.gray200}`,
              borderRadius: "4px",
          }}>
            <ListItemIcon>
              <FolderSmallIcon sx={{ color: sphereColors.gray800 }}/>
            </ListItemIcon>

            <ListItemText
              sx={{
                ml: "-20px",
                "& span": {
                  ...withEllipsis,
                },
              }}
              primary={importName}
            />

            <ListItemText
              sx={{
                ml: "6px",
                flexShrink: 0,
              }}
              primary={folderSize}
              primaryTypographyProps={{
                sx: {
                  textAlign: "right",
                  fontSize: "12px",
                },
              }}
            />
          </ListItem>
        );
      });
    });

    return items;
  }, [groupedFileInfos, isDropzoneLoading, setFileInfos, sizesTotal]);

  return (
    <Stack sx={{
      boxShadow: isScreenMdAndSmaller ? `0px 4px 4px ${sphereColors.gray100}` : undefined,
    }}>
      <Box
        onClick={() => {
          if (isScreenMdAndSmaller) {
            setIsCollapsed(!isCollapsed);
          }
        }}
        sx={{
          padding: "20px",
          cursor: isScreenMdAndSmaller ? "pointer" : undefined,
      }}>
        <Stack direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
        }}>
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <Box sx={{
              fontSize: "20px",
              fontWeight: "600",
            }}>
              Added data
            </Box>
            { isScreenMdAndSmaller &&
              <Box sx={{
                ml: "10px",
                fontSize: "14px",
              }}>
                (<var>{numInfos}</var> { numInfos === 1 ? "folder" : "folders" })
              </Box>
            }
          </Stack>

          { isScreenMdAndSmaller && (
            <FaroIconButton
              buttonSize="24px"
              iconSize="20px"
              component={isCollapsed ? ArrowUpIcon : ArrowDownIcon}
            />
          )}
        </Stack>
      </Box>

      <Collapse in={!isScreenMdAndSmaller || !isCollapsed}>
        <Box sx={{
          pl: "20px",
          pr: "20px",
          pb: "20px",
        }}>
          <DataManagementDropzone
            project={project}
            type="embedded"
            uploadedIdsMap={uploadedIdsMap}
            setFileInfos={setFileInfos}
            isFileExplorerOpen={isFileExplorerOpen}
            setIsFileExplorerOpen={setIsFileExplorerOpen}
            isLoading={isDropzoneLoading}
            setIsLoading={setIsDropzoneLoading}
          />

          <List
            dense={true}
            sx={{
              padding: "0px",
          }}>
            { listItems }
          </List>
        </Box>
      </Collapse>
    </Stack>
  );
}
